'use client'

import * as React from 'react'
import { useRouter, usePathname } from 'next/navigation'
import { Command } from 'cmdk'
import {
  AlignLeft,
  ArrowUpDown,
  Bell,
  Bookmark,
  Check,
  Clock,
  Compass,
  CornerDownLeft,
  CreditCard,
  Eye,
  FilePlus2,
  Heart,
  HelpCircle,
  History,
  Info,
  Keyboard,
  Languages,
  LayoutDashboard,
  Link2,
  List,
  LogIn,
  LogOut,
  Map,
  Monitor,
  Moon,
  Radar,
  Repeat,
  Search,
  Settings as SettingsIcon,
  Sun,
  Terminal,
  X,
} from 'lucide-react'
import { useUIStore, useSearchStore, useUserStore } from '@/lib/store'
import { useAuth } from '@/components/auth'
import { useI18n } from '@/lib/i18n'
import { api } from '@/lib/api'
import { SORT_OPTIONS } from '@/lib/constants'
import { emitGlobalToast } from '@/components/layout/global-toast'
import { applyRecentSearch, applySavedSearch, requestSearchRerun } from '@/lib/apply-search'
import type { SavedSearch } from '@/lib/types'

const ITEM =
  'flex items-center gap-2 px-3 py-2 rounded-lg text-foreground/80 cursor-pointer aria-selected:bg-primary/15 aria-selected:text-foreground'
const HINT = 'ml-auto text-xs text-muted-foreground'

/** Sentinel value for the run-a-search fallback item — the ⌘ makes it
 *  unmatchable by anything typed, so it always scores below real commands. */
const RUN_SEARCH_VALUE = '⌘run-search'

export function CommandPalette() {
  const router = useRouter()
  const pathname = usePathname()
  const { t } = useI18n()
  const { user, signIn, signOut } = useAuth()

  const {
    commandPaletteOpen,
    setCommandPaletteOpen,
    theme,
    setTheme,
    uiLanguage,
    setUiLanguage,
    setTourOpen,
  } = useUIStore()

  const {
    clearFilters,
    setViewMode,
    viewMode,
    activeTags,
    sort,
    setSort,
    filters,
    updateFilter,
    hiddenListings,
    showAllHidden,
    expandAllDescriptions,
    toggleExpandAllDescriptions,
  } = useSearchStore()

  const { recentSearches } = useUserStore()

  const inputRef = React.useRef<HTMLInputElement>(null)
  const [search, setSearch] = React.useState('')

  // Items that write the search store only make sense where a search is
  // actually rendered. Mounted globally (app/layout.tsx), the palette used to
  // offer "Clear filters" / "Save current search" on every page, where they
  // silently no-op — the save dialog listener lives on /search alone.
  const onSearchPage = pathname === '/search'

  // Saved searches are fetched on open: the zustand `savedSearches` array is
  // vestigial (every other consumer fetches into local state), so reading it
  // here would render an empty group.
  const [saved, setSaved] = React.useState<SavedSearch[]>([])
  const [savedCounts, setSavedCounts] = React.useState<Record<string, number>>({})

  React.useEffect(() => {
    if (!commandPaletteOpen || !user) return
    let cancelled = false
    Promise.all([
      api.getSavedSearches(),
      api.getNotificationCountsBySearch().catch(() => ({}) as Record<string, number>),
    ])
      .then(([searches, counts]) => {
        if (cancelled) return
        setSaved(searches)
        setSavedCounts(counts)
      })
      .catch(() => {
        if (!cancelled) setSaved([])
      })
    return () => {
      cancelled = true
    }
  }, [commandPaletteOpen, user])

  // Focus input when palette opens; reset the query so it never reopens
  // pre-filtered from last time.
  React.useEffect(() => {
    if (commandPaletteOpen) {
      setSearch('')
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [commandPaletteOpen])

  // Keyboard shortcuts: Cmd+K to toggle, ESC to close
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setCommandPaletteOpen(!commandPaletteOpen)
      }

      if (e.key === 'Escape' && commandPaletteOpen) {
        e.preventDefault()
        setCommandPaletteOpen(false)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [commandPaletteOpen, setCommandPaletteOpen])

  const handleSelect = (callback: () => void) => {
    callback()
    setCommandPaletteOpen(false)
  }

  const goto = (href: string) => handleSelect(() => router.push(href))

  /** Store was rewritten — re-run in place, or navigate so /search mounts. */
  const runSearch = () => {
    if (onSearchPage) requestSearchRerun()
    else router.push('/search')
  }

  const handleSaveSearch = () => {
    window.dispatchEvent(new CustomEvent('open-save-search-dialog'))
  }

  const handleOpenSavedSearches = () => {
    window.dispatchEvent(new CustomEvent('open-saved-searches-panel'))
  }

  const handleCopyLink = () => {
    navigator.clipboard
      .writeText(window.location.href)
      .then(() => emitGlobalToast(t('palette.copied', 'Search link copied')))
      .catch(() => emitGlobalToast(t('palette.copyFailed', 'Could not copy the link')))
  }

  const lastSearchUrl = React.useMemo(() => {
    if (!commandPaletteOpen || onSearchPage) return null
    try {
      return localStorage.getItem('roostr:lastSearchUrl')
    } catch {
      return null
    }
  }, [commandPaletteOpen, onSearchPage])

  if (!commandPaletteOpen) return null

  const trimmed = search.trim()
  const transaction = filters.transaction_type === 'sale' ? 'sale' : 'rent'

  return (
    <div className="fixed inset-0 z-50 bg-black/40" onClick={() => setCommandPaletteOpen(false)}>
      <div className="fixed left-1/2 top-1/4 -translate-x-1/2 w-full max-w-xl">
        <Command
          className="bg-background/95 backdrop-blur-xl border border-border/40 rounded-2xl shadow-lg overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center border-b border-border/30 px-4">
            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
            <Command.Input
              ref={inputRef}
              value={search}
              onValueChange={setSearch}
              placeholder={t('palette.placeholder', 'Search homes or type a command...')}
              className="flex-1 h-12 px-3 bg-transparent outline-none text-foreground placeholder:text-muted-foreground"
              autoFocus
            />
            <kbd className="hidden sm:inline-flex items-center gap-1 rounded bg-muted px-2 py-1 text-xs text-muted-foreground">
              ESC
            </kbd>
          </div>

          <Command.List className="max-h-80 overflow-y-auto p-2">
            <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
              {t('palette.noResults', 'No results found.')}
            </Command.Empty>

            {/* Recent searches */}
            {recentSearches.length > 0 && (
              <Command.Group heading={t('palette.recent', 'Recent searches')}>
                {recentSearches.slice(0, 5).map((recent) => (
                  <Command.Item
                    key={recent.id}
                    value={`recent ${recent.displayLabel}`}
                    onSelect={() =>
                      handleSelect(() => {
                        applyRecentSearch(recent)
                        runSearch()
                      })
                    }
                    className={ITEM}
                  >
                    <Clock className="h-4 w-4" />
                    <span className="truncate">{recent.displayLabel}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Saved searches & alerts */}
            <Command.Group heading={t('palette.savedGroup', 'Searches & Alerts')}>
              {saved.slice(0, 5).map((s) => {
                const count = savedCounts[s.id] ?? s.new_listings_count ?? 0
                return (
                  <Command.Item
                    key={s.id}
                    value={`saved ${s.name} ${s.original_query ?? ''}`}
                    onSelect={() =>
                      handleSelect(() => {
                        applySavedSearch(s)
                        runSearch()
                      })
                    }
                    className={ITEM}
                  >
                    <Radar className="h-4 w-4 text-primary" />
                    <span className="truncate">{s.name}</span>
                    {count > 0 && (
                      <span className="ml-auto rounded-full bg-primary/15 px-2 py-0.5 text-xs font-semibold text-primary">
                        {count} {t('palette.new', 'new')}
                      </span>
                    )}
                  </Command.Item>
                )
              })}

              {onSearchPage && (
                <Command.Item onSelect={() => handleSelect(handleSaveSearch)} className={ITEM}>
                  <Bookmark className="h-4 w-4 text-primary" />
                  <span>{t('palette.saveSearch', 'Save current search')}</span>
                </Command.Item>
              )}

              <Command.Item onSelect={() => handleSelect(handleOpenSavedSearches)} className={ITEM}>
                <Radar className="h-4 w-4" />
                <span>{t('palette.openSaved', 'Open Searches & Alerts')}</span>
              </Command.Item>
            </Command.Group>

            {/* Current search — only where a search is actually rendered. */}
            {onSearchPage && (
              <Command.Group heading={t('palette.thisSearch', 'This search')}>
                <Command.Item onSelect={() => handleSelect(clearFilters)} className={ITEM}>
                  <X className="h-4 w-4" />
                  <span>{t('palette.clearFilters', 'Clear all filters')}</span>
                  {activeTags.length > 0 && (
                    <span className={HINT}>
                      {activeTags.length} {t('palette.active', 'active')}
                    </span>
                  )}
                </Command.Item>

                <Command.Item
                  onSelect={() =>
                    handleSelect(() => setViewMode(viewMode === 'list' ? 'map' : 'list'))
                  }
                  className={ITEM}
                >
                  {viewMode === 'list' ? <Map className="h-4 w-4" /> : <List className="h-4 w-4" />}
                  <span>
                    {viewMode === 'list'
                      ? t('palette.toMap', 'Switch to Map view')
                      : t('palette.toList', 'Switch to List view')}
                  </span>
                </Command.Item>

                <Command.Item
                  onSelect={() =>
                    handleSelect(() =>
                      updateFilter('transaction_type', transaction === 'rent' ? 'sale' : 'rent'),
                    )
                  }
                  className={ITEM}
                >
                  <Repeat className="h-4 w-4" />
                  <span>
                    {transaction === 'rent'
                      ? t('palette.toSale', 'Switch to homes for sale')
                      : t('palette.toRent', 'Switch to homes for rent')}
                  </span>
                </Command.Item>

                <Command.Item
                  onSelect={() => handleSelect(toggleExpandAllDescriptions)}
                  className={ITEM}
                >
                  <AlignLeft className="h-4 w-4" />
                  <span>
                    {expandAllDescriptions
                      ? t('palette.collapseAll', 'Collapse all descriptions')
                      : t('palette.expandAll', 'Expand all descriptions')}
                  </span>
                </Command.Item>

                {hiddenListings.size > 0 && (
                  <Command.Item onSelect={() => handleSelect(showAllHidden)} className={ITEM}>
                    <Eye className="h-4 w-4" />
                    <span>{t('palette.showHidden', 'Show hidden listings')}</span>
                    <span className={HINT}>{hiddenListings.size}</span>
                  </Command.Item>
                )}

                <Command.Item onSelect={() => handleSelect(handleCopyLink)} className={ITEM}>
                  <Link2 className="h-4 w-4" />
                  <span>{t('palette.copyLink', 'Copy link to this search')}</span>
                </Command.Item>

                {/* The grid owns the shortcuts sheet (it is /search-only) —
                    reach it the same loose way the saved-searches panel is
                    opened from here. */}
                <Command.Item
                  onSelect={() =>
                    handleSelect(() =>
                      window.dispatchEvent(new CustomEvent('open-keyboard-shortcuts')),
                    )
                  }
                  className={ITEM}
                >
                  <Keyboard className="h-4 w-4" />
                  <span>{t('palette.shortcuts', 'Browse results with your keyboard')}</span>
                  <span className={HINT}>?</span>
                </Command.Item>
              </Command.Group>
            )}

            {/* Sort — /search only; re-sorting re-runs the query there. */}
            {onSearchPage && (
              <Command.Group heading={t('palette.sortGroup', 'Sort results')}>
                {SORT_OPTIONS.map((option) => (
                  <Command.Item
                    key={option.value}
                    value={`sort ${option.label}`}
                    onSelect={() => handleSelect(() => setSort(option.value))}
                    className={ITEM}
                  >
                    <ArrowUpDown className="h-4 w-4" />
                    <span>{option.label}</span>
                    {sort === option.value && <Check className="ml-auto h-4 w-4 text-primary" />}
                  </Command.Item>
                ))}
              </Command.Group>
            )}

            {/* Navigation */}
            <Command.Group heading={t('palette.goTo', 'Go to')}>
              {!onSearchPage && (
                <Command.Item onSelect={() => goto('/search')} className={ITEM}>
                  <Search className="h-4 w-4" />
                  <span>{t('palette.gotoSearch', 'Search')}</span>
                </Command.Item>
              )}

              {lastSearchUrl && (
                <Command.Item onSelect={() => goto(lastSearchUrl)} className={ITEM}>
                  <History className="h-4 w-4" />
                  <span>{t('palette.lastSearch', 'Back to my last search')}</span>
                </Command.Item>
              )}

              <Command.Item onSelect={() => goto('/liked')} className={ITEM}>
                <Heart className="h-4 w-4" />
                <span>{t('palette.gotoLiked', 'Liked listings')}</span>
              </Command.Item>

              {user && (
                <>
                  <Command.Item onSelect={() => goto('/searches')} className={ITEM}>
                    <Radar className="h-4 w-4" />
                    <span>{t('palette.gotoSearches', 'My searches')}</span>
                  </Command.Item>

                  <Command.Item onSelect={() => goto('/notifications')} className={ITEM}>
                    <Bell className="h-4 w-4" />
                    <span>{t('palette.gotoNotifications', 'Notifications')}</span>
                  </Command.Item>

                  <Command.Item onSelect={() => goto('/my-listings')} className={ITEM}>
                    <LayoutDashboard className="h-4 w-4" />
                    <span>{t('palette.gotoMyListings', 'My listings')}</span>
                  </Command.Item>

                  <Command.Item onSelect={() => goto('/post')} className={ITEM}>
                    <FilePlus2 className="h-4 w-4" />
                    <span>{t('palette.gotoPost', 'Post a listing')}</span>
                  </Command.Item>

                  <Command.Item onSelect={() => goto('/settings')} className={ITEM}>
                    <SettingsIcon className="h-4 w-4" />
                    <span>{t('palette.gotoSettings', 'Settings')}</span>
                  </Command.Item>
                </>
              )}

              <Command.Item onSelect={() => goto('/pricing')} className={ITEM}>
                <CreditCard className="h-4 w-4" />
                <span>{t('palette.gotoPricing', 'Pricing')}</span>
              </Command.Item>

              <Command.Item onSelect={() => goto('/about')} className={ITEM}>
                <Info className="h-4 w-4" />
                <span>{t('palette.gotoAbout', 'About')}</span>
              </Command.Item>
            </Command.Group>

            {/* Language */}
            <Command.Group heading={t('palette.language', 'Language')}>
              {(
                [
                  ['cs', 'Čeština'],
                  ['en', 'English'],
                ] as const
              ).map(([value, label]) => (
                <Command.Item
                  key={value}
                  value={`language ${label}`}
                  onSelect={() => handleSelect(() => setUiLanguage(value))}
                  className={ITEM}
                >
                  <Languages className="h-4 w-4" />
                  <span>{label}</span>
                  {uiLanguage === value && <Check className="ml-auto h-4 w-4 text-primary" />}
                </Command.Item>
              ))}
            </Command.Group>

            {/* Appearance — one explicit item per theme (the old single
                "switch to next" cycler made it look like themes were
                missing: typing "mode" only ever surfaced one option). */}
            <Command.Group heading={t('palette.appearance', 'Appearance')}>
              {(
                [
                  ['light', 'Light', Sun],
                  ['dark', 'Dark', Moon],
                  ['hacker', 'Hacker', Terminal],
                  ['system', 'System', Monitor],
                ] as const
              ).map(([value, fallback, Icon]) => {
                // Localized theme name — Czech needs a lowercase adjective for
                // "Přepnout na světlý režim" to read naturally.
                const label = t(`palette.theme.${value}`, fallback)
                return (
                  <Command.Item
                    key={value}
                    value={`theme mode ${label} ${fallback}`}
                    onSelect={() => handleSelect(() => setTheme(value))}
                    className={ITEM}
                  >
                    <Icon className="h-4 w-4" />
                    <span>
                      {t('palette.switchTo', 'Switch to')} {label} {t('palette.mode', 'mode')}
                    </span>
                    {theme === value && (
                      <span className={HINT}>{t('palette.current', 'current')}</span>
                    )}
                  </Command.Item>
                )
              })}
            </Command.Group>

            {/* Help & account */}
            <Command.Group heading={t('palette.helpGroup', 'Help & account')}>
              <Command.Item onSelect={() => handleSelect(() => setTourOpen(true))} className={ITEM}>
                <HelpCircle className="h-4 w-4" />
                <span>{t('palette.tour', 'How Roostr works')}</span>
              </Command.Item>

              {user ? (
                <Command.Item onSelect={() => handleSelect(() => void signOut())} className={ITEM}>
                  <LogOut className="h-4 w-4" />
                  <span>{t('palette.signOut', 'Sign out')}</span>
                </Command.Item>
              ) : (
                <Command.Item onSelect={() => handleSelect(() => signIn())} className={ITEM}>
                  <LogIn className="h-4 w-4 text-primary" />
                  <span>{t('palette.signIn', 'Sign in or create an account')}</span>
                </Command.Item>
              )}
            </Command.Group>

            {/* Run the typed text as a real search — the fallback, rendered
                last. `forceMount` keeps it visible whatever the filter does,
                and the sentinel `value` scores 0 against anything a human
                would type, so a genuinely matching command always outranks it.
                Type "dark" → the theme item is selected; type "balcony" →
                nothing else matches, so this is the only item and Enter
                searches. */}
            {trimmed.length > 0 && (
              <Command.Group forceMount heading={t('palette.searchGroup', 'Search')}>
                <Command.Item
                  forceMount
                  value={RUN_SEARCH_VALUE}
                  onSelect={() =>
                    handleSelect(() => router.push(`/search?q=${encodeURIComponent(trimmed)}`))
                  }
                  className={ITEM}
                >
                  <Compass className="h-4 w-4 text-primary" />
                  <span className="truncate">
                    {t('palette.searchFor', 'Search homes for')} “{trimmed}”
                  </span>
                  <CornerDownLeft className="ml-auto h-3.5 w-3.5 text-muted-foreground" />
                </Command.Item>
              </Command.Group>
            )}
          </Command.List>

          <div className="border-t border-border/30 px-4 py-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-4">
              <span>
                <kbd className="px-1 py-0.5 bg-muted/60 rounded">↑↓</kbd>{' '}
                {t('palette.navigate', 'Navigate')}
              </span>
              <span>
                <kbd className="px-1 py-0.5 bg-muted/60 rounded">↵</kbd>{' '}
                {t('palette.select', 'Select')}
              </span>
              <span>
                <kbd className="px-1 py-0.5 bg-muted/60 rounded">esc</kbd>{' '}
                {t('palette.close', 'Close')}
              </span>
            </span>
          </div>
        </Command>
      </div>
    </div>
  )
}
